import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { announcementService } from "@/features/announcements/services/feedService";
import type { PollResults } from "@/features/announcements/types/feed";
import { Button } from "@/shared/components/ui/Button";
import { Modal } from "@/shared/components/ui/Modal";
import { Spinner } from "@/shared/components/ui/Spinner";
import { formatDateTime } from "@/shared/utils/formatters";

interface PollResultsModalProps {
  isOpen: boolean;
  rowId: number;
  canWrite: boolean;
  onClose: () => void;
  onLockChange?: (locked: boolean) => void;
}

export const PollResultsModal = ({
  isOpen,
  rowId,
  canWrite,
  onClose,
  onLockChange,
}: PollResultsModalProps) => {
  const { t, i18n } = useTranslation();
  const [results, setResults] = useState<PollResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError("");
    announcementService.getPollResults(rowId)
      .then(setResults)
      .catch((err: unknown) => {
        const body = axios.isAxiosError(err) ? err.response?.data : undefined;
        setError(body?.error || t("feed.poll.not_available"));
      })
      .finally(() => setLoading(false));
  }, [isOpen, rowId, t]);

  const toggleLock = async () => {
    if (!results) return;
    const next = !results.is_locked;
    setResults({ ...results, is_locked: next, is_open: next ? false : results.is_open });
    setBusy(true);
    try {
      await announcementService.setPollLock(rowId, next);
      onLockChange?.(next);
    } catch (err) {
      setResults({ ...results });
      const body = axios.isAxiosError(err) ? err.response?.data : undefined;
      setError(body?.error || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("feed.poll.results")} size="lg">
      {loading ? <Spinner /> : error ? (
        <p className="py-6 text-center text-sm text-red-600">{error}</p>
      ) : results && (
        <div className="space-y-5">
          <div>
            <h3 className="font-semibold text-gray-900">{results.question}</h3>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                [t("feed.poll.respondents"), results.total_respondents],
                [t("feed.poll.audience"), results.audience_count],
                [t("feed.poll.response_rate"), `${Math.round(results.response_rate * 100)}%`],
                [t("feed.poll.not_responded"), results.not_responded],
              ].map(([label, value]) => (
                <div key={String(label)} className="rounded-lg bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-lg font-semibold text-gray-900">{value}</p>
                </div>
              ))}
            </div>
          </div>
          {results.total_respondents === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">{t("feed.poll.no_responses")}</p>
          ) : (
            <div className="space-y-3">
              {[...results.options].sort((a, b) => a.sort_order - b.sort_order).map((option) => {
                const open = expanded.has(option.option_id);
                return <div key={option.option_id} className="rounded-lg border border-gray-200 p-3">
                  <button type="button" className="w-full text-left" onClick={() => setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(option.option_id)) next.delete(option.option_id); else next.add(option.option_id);
                    return next;
                  })}>
                    <div className="flex justify-between gap-3 text-sm"><span>{option.label}</span><span className="font-medium">{option.count} · {Math.round(option.percent * 100)}%</span></div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100"><div className="h-full bg-indigo-500" style={{ width: `${Math.min(100, option.percent * 100)}%` }} /></div>
                  </button>
                  {open && <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
                    {option.respondents.map((person) => <div key={person.id} className="flex justify-between gap-3 py-2 text-sm">
                      <div>
                        <p className="text-gray-800">{person.name}</p>
                        <p className="text-xs text-gray-500">{person.company || "—"}</p>
                        {person.note && <p className="mt-1 whitespace-pre-wrap rounded bg-gray-50 px-2 py-1 text-xs italic text-gray-700">{person.note}</p>}
                      </div>
                      <span className="text-xs text-gray-400">{formatDateTime(person.responded_at)}</span>
                    </div>)}
                  </div>}
                </div>;
              })}
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
            <Button variant="secondary" onClick={() => announcementService.exportPollResults(rowId, i18n.language)}>{t("feed.poll.export_csv")}</Button>
            {canWrite && <Button variant="secondary" loading={busy} onClick={toggleLock}>{t(results.is_locked ? "feed.poll.unlock" : "feed.poll.lock")}</Button>}
            <Button onClick={onClose}>{t("common.close")}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
